import { Dispatch, useEffect, useState } from 'react';
import { connect, ConnectedProps, useSelector } from 'react-redux';
import classNames from 'classnames';
import { Action } from 'redux';

import lang from '../../../lib/language';
import { MarketCountryCode, StockExchangeCodeType } from '../../../lib/models/fundamental/types';
import { ReportingCall } from '../../../lib/models/reporting/types';
import { RootState } from '../../../lib/store';
import { symbolStatusSubscription } from '../../../lib/store/market-data/subscriptionHelpers';
import { getOpenPositions } from '../../../lib/store/reporting';
import { MyStocksContent, OpenPosition } from '../../../lib/store/reporting/types';
import { hasValidCookie } from '../../../lib/store/selectors';
import TradePriceCache from '../../../lib/store-util/TradePriceCache';
import { uniq } from '../../../lib/util/collectionHelpers';
import { isCallStatusReady } from '../../../lib/util/error-handling/StatusByCallHelpers';
import {
  calculateMyStockContent,
  mapMyStocksTableContent,
  sortOpenPositions,
} from '../../../lib/util/my-account-helpers';
import { isEmpty } from '../../../lib/util/typeGuards';

import NavButtons from '../../../components/NavigationButtons/NavButtons';
import { useIsMobile } from '../../../hooks';
import MyStocksTable from '../MyStocks/MyStocksTable';
import PendingOrders from '../PendingOrders/PendingOrders';

import styles from './MyAccountTableContent.module.scss';

type OwnProps = {
  isMyAccountInitiliazed: boolean;
  isUserVerified: boolean;
};

type Props = OwnProps & ReduxProps;

const MyAccountTableContent = ({
  isMyAccountInitiliazed,
  isUserVerified,
  openPositions,
  pendingOrders,
  companyLogoAndName,
  hasStocksFetched,
  hasMyStocksOrderUpdate,
  fetchOpenPositions,
}: Props) => {
  const hasCookieSession = useSelector<RootState, boolean>((state: RootState) => hasValidCookie(state));
  const [sortedOpenPositions, setSortedOpenPositions ] = useState<OpenPosition[]>([]);
  const [positionTableContent, setPositionTableContent ] = useState<MyStocksContent[]>([]);
  const [currentTab, setCurrentTab] = useState<number>(0);
  const [hasSetInitialTab, setHasSetInitialTab] = useState<boolean>(false);

  const tabs = [
    { text: lang.commonMyAccountButtonMyStocks(),
      component: <MyStocksTable
        tableData={openPositions?.length > 0 ? positionTableContent : []}
      />,
    },
    {
      text: lang.commonMyAccountButtonPendingOrders(),
      component: <PendingOrders />,
    },
  ];

  const tradePricesCache = TradePriceCache.useCustom(data => data);
  const { isMobile, isTablet } = useIsMobile();

  useEffect(() => {
    if (!openPositions.length) {
      fetchOpenPositions();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (openPositions.length && hasStocksFetched && !hasMyStocksOrderUpdate) {
      const positionsData = calculateMyStockContent(
        lang.commonAccountBreakdownTotal(), openPositions, tradePricesCache,
      );
      setSortedOpenPositions(sortOpenPositions(positionsData.data));
    }
  }, [openPositions, hasStocksFetched, tradePricesCache, hasMyStocksOrderUpdate]);

  useEffect(() => {
    if (openPositions.length && !isEmpty(companyLogoAndName) && hasStocksFetched && !hasMyStocksOrderUpdate) {
      setPositionTableContent(
        mapMyStocksTableContent(
          sortedOpenPositions,
          tradePricesCache,
          companyLogoAndName,
        ),
      );
    }
  }, [
    companyLogoAndName, hasMyStocksOrderUpdate, hasStocksFetched, openPositions, sortedOpenPositions, tradePricesCache,
  ]);

  useEffect(() => {
    if (!hasStocksFetched || !hasCookieSession) {
      return () => {};
    }

    const openPosSymols = openPositions.map(el => el.symbol);
    const pendingSymbols = pendingOrders.map(el => el.symbol);
    const symbolsList = uniq([ ...openPosSymols, ...pendingSymbols ]);
    const mappedSymbols = symbolsList.map(item => ({
      symbol: item,
      stockExchangeCode: StockExchangeCodeType.US_NASDAQ,
      countryDesignation: MarketCountryCode.UnitedStates,
    }));

    if (isMyAccountInitiliazed) {
      symbolStatusSubscription(mappedSymbols);
      if (!openPositions.length && !!pendingOrders.length) {
        setCurrentTab(1);
      }
    }

    return () => {
      if (mappedSymbols.length > 0) {
        symbolStatusSubscription(mappedSymbols, true);
      }
    };
  }, [hasStocksFetched, isMyAccountInitiliazed, openPositions, pendingOrders, hasCookieSession, companyLogoAndName]);

  useEffect(() => {
    if (!hasSetInitialTab && openPositions.length === 0 && pendingOrders && pendingOrders.length > 0) {
      setCurrentTab(1);
      setHasSetInitialTab(true);
    }
  }, [pendingOrders, hasSetInitialTab, openPositions.length]);

  const handleCurrentTabIndex = (index: number) => {
    !index
      ? setCurrentTab(0)
      : setCurrentTab(1);
  };

  return (
    <div
      className={styles.wrapperLayout}
    >
      <div className={classNames(styles.initialTableHeight,
        isUserVerified ? styles.wrapperFirstSection : styles.wrapperFirstSectionFullWidth)}
      >
        <div className={styles.wrapperChartMobile}>
          <div>
            <NavButtons
              tabs={tabs}
              sendCurrentTabIndex={handleCurrentTabIndex}
              initialActiveTab={currentTab}
              fullWidthButtons={isMobile || isTablet}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  openPositions: state.reporting.openPositions,
  pendingOrders: state.reporting.pendingOrders,
  companyLogoAndName: state.fundamental.companyLogoAndName,
  hasStocksFetched: isCallStatusReady(ReportingCall.getOpenPositions, state.reporting.statusByCall),
  hasMyStocksOrderUpdate: state.reporting.hasMyStocksOrderUpdate,
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  fetchOpenPositions: () => dispatch(getOpenPositions()),
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type ReduxProps = ConnectedProps<typeof connector>;

export default connector(MyAccountTableContent);
